import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { z } from "zod"

import BankLocationSelector from "../forms/components/BankLocationSelector"
import { useBankCompanies } from "../hooks/useBankCompanies"
import { BillingLocationSchema } from "../schemas/BillingLocationSchema"

import { AutoComplete } from "@/components/common/Autocomplete"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import LocationSelector from "@/components/Forms/LocationSelector"
import { type CompanyPaymentType, PaymentMethod } from "@/graphql/codegen/graphql"

const EditPaymentTypeSchema = BillingLocationSchema.extend({
  bankId: z.string().optional(),
  bankLocationId: z.string().optional(),
})

type EditPaymentTypeFormValues = z.infer<typeof EditPaymentTypeSchema>

interface EditPaymentTypeProps {
  companyId: string
  paymentType: CompanyPaymentType
  onClose: () => void
  onSave: (data: { locationIds: string[]; bankId?: string; bankLocationId?: string }) => void
}

const EditPaymentType: React.FC<EditPaymentTypeProps> = ({ companyId, paymentType, onClose, onSave }) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const {
    banks,
    isLoading: isBanksLoading,
    setSearchTerm: setBankSearchTerm,
  } = useBankCompanies({ withLocations: true })

  const selectedLocationIds =
    (paymentType.billingLocations?.map((location) => location?.locationId).filter(Boolean) as string[]) || []

  const isBankingPaymentType =
    paymentType.paymentMethods?.[0]?.method === PaymentMethod.Wire ||
    paymentType.paymentMethods?.[0]?.method === PaymentMethod.Ach

  const hasNoBank = isBankingPaymentType && !paymentType.bankId
  const hasNoBankLocation = isBankingPaymentType && !paymentType.bankLocationId

  const methods = useForm<EditPaymentTypeFormValues>({
    resolver: zodResolver(EditPaymentTypeSchema),
    mode: "onChange",
    defaultValues: {
      locationId: selectedLocationIds[0] || "",
      bankId: paymentType.bankId || "",
      bankLocationId: paymentType.bankLocationId || "",
    },
  })

  const {
    handleSubmit,
    watch,
    formState: { isDirty, isValid },
  } = methods

  const watchedBankId = watch("bankId")

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const onSubmit = (data: EditPaymentTypeFormValues) => {
    const locationIds = data.locationId ? [data.locationId] : []
    onSave({
      locationIds,
      bankId: hasNoBank ? data.bankId : undefined,
      bankLocationId: hasNoBankLocation ? data.bankLocationId : undefined,
    })
  }

  const canSave = isDirty && isValid

  const getDialogTitle = () => {
    if (isBankingPaymentType) {
      return hasNoBankLocation ? "Edit Payment Type" : "Edit Billing Information"
    }
    return "Edit Billing Information"
  }

  return (
    <>
      {!isCancelConfirmationOpen && (
        <Dialog open onClose={handleCancel} maxWidth='sm' fullWidth>
          <DialogHeader title={getDialogTitle()} onClose={handleCancel} />
          <DialogContent classes={{ root: "px-10" }}>
            <FormProvider {...methods}>
              <Box className='space-y-4'>
                <LocationSelector
                  name='locationId'
                  label='Company Billing Location'
                  companyId={companyId}
                  placeholder='Select Location'
                />

                {hasNoBank && (
                  <AutoComplete
                    control={methods.control}
                    name='bankId'
                    label='Bank'
                    placeholder='Search Bank'
                    options={banks.filter((bank) => bank.locations?.length).map((bank) => bank.companyId)}
                    getOptionLabel={(bankId) => {
                      const bank = banks.find((b) => b.companyId === bankId)
                      return bank?.name ?? ""
                    }}
                    onInputChange={setBankSearchTerm}
                    defaultEmptyState={""}
                    loading={isBanksLoading}
                  />
                )}

                {hasNoBankLocation && (
                  <BankLocationSelector bankId={watchedBankId} name='bankLocationId' label='Bank Location' />
                )}
              </Box>
            </FormProvider>
          </DialogContent>

          <DialogActions className='justify-between px-10 pb-10 pt-4'>
            <Button onClick={handleCancel} variant='outlined' color='primary'>
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} variant='contained' color='primary' disabled={!canSave}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isCancelConfirmationOpen && (
        <CancelDialog onConfirm={onClose} onClose={() => setIsCancelConfirmationOpen(false)} />
      )}
    </>
  )
}

export default EditPaymentType
